import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle, Trash, Archive, ClockCounterClockwise, Copy, Eye, PencilSimple,
  DotsSixVertical, BookOpen, ArrowClockwise, CaretLeft, FloppyDisk, CaretUp, CaretDown } from "@phosphor-icons/react";
import ChecklistWizard from "../components/ChecklistWizard";
import { fmtDate } from "../lib/date";
import { ANSWER_TYPES, SEVERITIES, METER_UNITS, sevBadgeCls, statusBadgeCls, statusLabel, typeLabel } from "../lib/checklist";

const TABS = [["active", "Active"], ["drafts", "Drafts"], ["archived", "Archived"]];

export default function ChecksPage() {
  const [tab, setTab] = useState("active");
  const [families, setFamilies] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createMenu, setCreateMenu] = useState(false);
  const [history, setHistory] = useState(null);   // { name, versions }
  const [preview, setPreview] = useState(null);    // template
  const [editPrompt, setEditPrompt] = useState(null); // family needing new version

  const reqRef = useRef(0);
  const load = useCallback(() => {
    const my = ++reqRef.current;
    setFamilies([]);
    api.get(`/checks/families?tab=${tab}`).then((r) => { if (my === reqRef.current) setFamilies(r.data); }).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (fam) => {
    if (fam.draft_id) { setEditingId(fam.draft_id); return; }
    // A draft may have appeared since the list was fetched — check version history first.
    try {
      const vs = (await api.get(`/checks/families/${fam.family_id}/versions`)).data;
      const draft = vs.find((v) => v.status === "draft");
      if (draft) { setEditingId(draft.id); return; }
    } catch { /* fall through */ }
    setEditPrompt(fam);
  };
  const createNewVersion = async (fam) => {
    try {
      const r = await api.post(`/checks/templates/${fam.current_id}/new-version`);
      setEditPrompt(null); setEditingId(r.data.id); toast.success(`Draft V${r.data.version} created`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.draft_id) { setEditPrompt(null); setEditingId(d.draft_id); toast.info(d.message); }
      else toast.error("Failed");
    }
  };
  const startBlank = async () => {
    try { const r = await api.post("/checks/templates", { name: "New Checklist", validity_hours: 24 }); setCreateMenu(false); setEditingId(r.data.id); }
    catch { toast.error("Failed"); }
  };
  const duplicate = async (fam) => {
    try { await api.post(`/checks/templates/${fam.current_id || fam.draft_id}/duplicate`); toast.success("Duplicated as a new checklist (V1 draft)"); setTab("drafts"); }
    catch { toast.error("Failed"); }
  };
  const archive = async (fam) => {
    if (!window.confirm(`Archive "${fam.name}"? It will be hidden from normal use but all history is kept.`)) return;
    try { await api.post(`/checks/templates/${fam.current_id || fam.draft_id}/archive`); toast.success("Checklist archived"); load(); }
    catch { toast.error("Failed"); }
  };
  const restore = async (fam) => {
    try { await api.post(`/checks/templates/${fam.current_id || fam.draft_id}/restore`); toast.success("Checklist restored"); load(); }
    catch { toast.error("Failed"); }
  };
  const showHistory = async (fam) => {
    const r = await api.get(`/checks/families/${fam.family_id}/versions`);
    setHistory({ name: fam.name, versions: r.data });
  };
  const showPreview = async (fam) => {
    const r = await api.get(`/checks/templates/${fam.current_id || fam.draft_id}`);
    setPreview(r.data);
  };

  if (editingId) return <ChecklistEditor id={editingId} onBack={() => { setEditingId(null); load(); }} />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Checklists</h1>
        <div className="relative">
          <Button data-testid="create-checklist" onClick={() => setCreateMenu((v) => !v)} className="h-11 bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={18} weight="bold" className="mr-1" />Create Checklist</Button>
          {createMenu && (
            <div className="absolute right-0 mt-1 z-50 w-56 bg-white border-2 border-slate-200 rounded-lg shadow-lg overflow-hidden">
              <button data-testid="create-wizard" onClick={() => { setCreateMenu(false); setWizardOpen(true); }} className="w-full text-left px-3 py-2.5 hover:bg-slate-50">
                <div className="font-body font-semibold text-slate-800 text-sm">Quick Wizard</div>
                <div className="text-xs text-slate-500">Recommended · pick type &amp; tick questions</div>
              </button>
              <button data-testid="create-blank" onClick={startBlank} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-t border-slate-100">
                <div className="font-body font-semibold text-slate-800 text-sm">Start Blank</div>
                <div className="text-xs text-slate-500">Build from scratch</div>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="checklist-tabs">
        {TABS.map(([v, label]) => (
          <button key={v} data-testid={`checklist-tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === v ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {families.map((f) => (
          <div key={f.family_id} data-testid={`family-card-${f.family_id}`} className="bg-white border-2 border-slate-200 rounded-lg p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-body font-semibold text-slate-900 truncate">{f.name}</div>
                <div className="font-mono text-xs text-slate-500">
                  {f.current_version ? `Current: V${f.current_version}` : "No published version"}
                  {f.draft_version ? ` · Draft V${f.draft_version}` : ""}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
              {f.asset_kind && <span className="capitalize"><b>Type:</b> {f.asset_kind}</span>}
              {f.frequency_days && <span><b>Every:</b> {f.frequency_days}d</span>}
              <span><b>Questions:</b> {f.question_count}</span>
            </div>
            {f.last_updated && <div className="text-xs text-slate-400">Updated {fmtDate(f.last_updated)}</div>}
            <div className="flex flex-wrap gap-1.5 mt-1">
              {tab !== "archived" ? (
                <>
                  <Button data-testid={`fam-edit-${f.family_id}`} onClick={() => openEdit(f)} className="h-8 bg-slate-900 hover:bg-slate-800 font-semibold text-xs"><PencilSimple size={14} weight="bold" className="mr-1" />Edit</Button>
                  <Button data-testid={`fam-preview-${f.family_id}`} variant="outline" onClick={() => showPreview(f)} className="h-8 font-semibold text-xs"><Eye size={14} weight="bold" className="mr-1" />Preview</Button>
                  <Button data-testid={`fam-history-${f.family_id}`} variant="outline" onClick={() => showHistory(f)} className="h-8 font-semibold text-xs"><ClockCounterClockwise size={14} weight="bold" className="mr-1" />History</Button>
                  <Button data-testid={`fam-duplicate-${f.family_id}`} variant="outline" onClick={() => duplicate(f)} className="h-8 font-semibold text-xs"><Copy size={14} weight="bold" className="mr-1" />Duplicate</Button>
                  <Button data-testid={`fam-archive-${f.family_id}`} variant="outline" onClick={() => archive(f)} className="h-8 font-semibold text-xs text-red-600"><Archive size={14} weight="bold" className="mr-1" />Archive</Button>
                </>
              ) : (
                <>
                  <Button data-testid={`fam-history-${f.family_id}`} variant="outline" onClick={() => showHistory(f)} className="h-8 font-semibold text-xs"><ClockCounterClockwise size={14} weight="bold" className="mr-1" />History</Button>
                  <Button data-testid={`fam-restore-${f.family_id}`} onClick={() => restore(f)} className="h-8 bg-emerald-600 hover:bg-emerald-700 font-semibold text-xs"><ArrowClockwise size={14} weight="bold" className="mr-1" />Restore</Button>
                </>
              )}
            </div>
          </div>
        ))}
        {families.length === 0 && <p className="text-slate-400 text-sm">No checklists in this tab.</p>}
      </div>

      <ChecklistWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={(id) => { setWizardOpen(false); setEditingId(id); }} />

      <Dialog open={!!editPrompt} onOpenChange={(o) => !o && setEditPrompt(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="edit-published-prompt" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Editing a published checklist</DialogTitle></DialogHeader>
          <p className="font-body text-sm text-slate-600">This checklist is published and locked so completed checks keep pointing to the version used at the time. Create a new version to make changes — it becomes the next Draft.</p>
          <div className="flex gap-2 mt-2">
            <Button data-testid="confirm-new-version" onClick={() => createNewVersion(editPrompt)} className="bg-emerald-600 hover:bg-emerald-700 font-semibold flex-1">Create New Version</Button>
            <Button variant="outline" onClick={() => setEditPrompt(null)} className="font-semibold">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="version-history" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{history?.name} — Version History</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-1.5">
            {(history?.versions || []).map((v) => (
              <div key={v.id} data-testid={`history-v${v.version}`} className="flex items-center justify-between border-2 border-slate-200 rounded px-3 py-2">
                <span className="font-mono text-sm font-bold text-slate-700">V{v.version}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${statusBadgeCls(v.status)}`}>{statusLabel(v.status, v.is_current)}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="checklist-preview" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{preview?.name} <span className="font-mono text-sm text-slate-400">v{preview?.version}</span></DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">How the operative will see it</p>
          <div className="flex flex-col gap-2">
            {(preview?.questions || []).map((q, i) => (
              <div key={q.id} className={`rounded-lg border-2 p-3 ${q.is_critical ? "border-red-200" : "border-slate-200"}`}>
                <div className="font-body font-semibold text-slate-800 text-sm">{i + 1}. {q.prompt}
                  {q.fail_severity && <span className={`ml-2 text-[9px] font-bold uppercase px-1 rounded ${sevBadgeCls(q.fail_severity)}`}>{q.fail_severity}</span>}</div>
                <div className="text-xs text-slate-400 font-mono mt-1">{typeLabel(q.answer_type)}{q.unit ? ` · ${q.unit}` : ""}</div>
              </div>
            ))}
            {(preview?.questions || []).length === 0 && <p className="text-sm text-slate-400">No questions.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChecklistEditor({ id, onBack }) {
  const [t, setT] = useState(null);
  const [meta, setMeta] = useState({ name: "", validity_hours: 24, frequency_days: "" });
  const [qForm, setQForm] = useState(newQ());
  const [libOpen, setLibOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selTypes, setSelTypes] = useState({});   // multi-select asset types
  const [selAssets, setSelAssets] = useState({}); // multi-select assets
  const [assetQ, setAssetQ] = useState("");
  const [followLatest, setFollowLatest] = useState(true);

  const reload = useCallback(async () => {
    const r = await api.get(`/checks/templates/${id}`);
    setT(r.data);
    setMeta({ name: r.data.name || "", validity_hours: r.data.validity_hours || 24, frequency_days: r.data.frequency_days || "" });
  }, [id]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    api.get("/config/asset-types").then((r) => setAssetTypes(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  if (!t) return <p className="text-slate-500">Loading…</p>;
  const editable = t.status === "draft";

  const saveMeta = async () => {
    try { await api.put(`/checks/templates/${id}`, { name: meta.name, validity_hours: Number(meta.validity_hours) || 24, frequency_days: meta.frequency_days === "" ? null : Number(meta.frequency_days) }); toast.success("Saved"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addQuestion = async () => {
    try { await api.post(`/checks/templates/${id}/questions`, { ...qForm, sort_order: t.questions.length }); setQForm(newQ()); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveQ = async (qid, patch) => { try { await api.put(`/checks/questions/${qid}`, patch); toast.success("Saved"); reload(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const delQ = async (qid) => { try { await api.delete(`/checks/questions/${qid}`); reload(); } catch { toast.error("Failed"); } };
  const dupQ = async (qid) => { try { await api.post(`/checks/questions/${qid}/duplicate`); reload(); } catch { toast.error("Failed"); } };
  const publish = async () => { try { await api.post(`/checks/templates/${id}/publish`); toast.success("Published as Current version"); reload(); } catch { toast.error("Failed"); } };

  const persistOrder = async (arr) => {
    try { await api.post(`/checks/templates/${id}/reorder`, { order: arr.map((q) => q.id) }); }
    catch { toast.error("Reorder failed"); reload(); }
  };
  const move = (idx, dir) => {
    const arr = [...t.questions]; const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setT({ ...t, questions: arr }); persistOrder(arr);
  };
  const onDrop = (idx) => {
    if (dragIdx == null || dragIdx === idx) return;
    const arr = [...t.questions]; const [m] = arr.splice(dragIdx, 1); arr.splice(idx, 0, m);
    setDragIdx(null); setT({ ...t, questions: arr }); persistOrder(arr);
  };

  const assignedTypeIds = new Set((t.assignments || []).filter((a) => a.asset_type_id).map((a) => a.asset_type_id));
  const assignedAssetIds = new Set((t.assignments || []).filter((a) => a.asset_id).map((a) => a.asset_id));
  const typeChosen = Object.keys(selTypes).filter((k) => selTypes[k]);
  const assetChosen = Object.keys(selAssets).filter((k) => selAssets[k]);
  const bulkAssign = async () => {
    if (typeChosen.length === 0 && assetChosen.length === 0) return;
    try {
      const r = await api.post(`/checks/templates/${id}/assign-bulk`, { asset_type_ids: typeChosen, asset_ids: assetChosen, follow_latest: followLatest });
      toast.success(r.data.created ? `Assigned to ${r.data.created} target${r.data.created === 1 ? "" : "s"}` : "Already assigned");
      setSelTypes({}); setSelAssets({}); setAssetQ(""); reload();
    } catch { toast.error("Failed"); }
  };
  const unassign = async (aid) => { try { await api.delete(`/checks/assignments/${aid}`); reload(); } catch { toast.error("Failed"); } };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Button data-testid="editor-back" variant="outline" onClick={onBack} className="font-semibold"><CaretLeft size={16} weight="bold" className="mr-1" />Back</Button>
        <h1 className="font-display text-2xl font-black text-slate-900">{t.name} <span className="font-mono text-sm text-slate-500">v{t.version}</span></h1>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${statusBadgeCls(t.status)}`}>{statusLabel(t.status, t.is_current)}</span>
        <div className="ml-auto flex gap-2">
          {editable && <Button data-testid="editor-publish" onClick={publish} className="bg-emerald-600 hover:bg-emerald-700 font-semibold"><CheckCircle size={16} weight="bold" className="mr-1" />Publish</Button>}
        </div>
      </div>

      {!editable && (
        <div data-testid="editor-locked" className="bg-amber-50 border-l-8 border-amber-500 px-4 py-2 rounded text-sm text-amber-800 font-semibold">
          This version is {t.status}. Go back and use <b>Create New Version</b> to make changes.
        </div>
      )}

      {editable && (
        <div className="bg-white border-2 border-slate-200 rounded-lg p-4 grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs font-semibold text-slate-600">Name</label><Input data-testid="editor-name" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Frequency (days, blank = ad hoc)</label><Input data-testid="editor-freq" type="number" value={meta.frequency_days} onChange={(e) => setMeta({ ...meta, frequency_days: e.target.value })} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Validity (hours)</label><Input data-testid="editor-validity" type="number" value={meta.validity_hours} onChange={(e) => setMeta({ ...meta, validity_hours: e.target.value })} /></div>
          <div className="sm:col-span-3"><Button data-testid="editor-save-meta" onClick={saveMeta} variant="outline" className="font-semibold"><FloppyDisk size={16} weight="bold" className="mr-1" />Save details</Button></div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {t.questions.map((q, i) => (
          <QuestionRow key={q.id} q={q} index={i} total={t.questions.length} editable={editable}
            onMove={move} onSave={saveQ} onDelete={delQ} onDuplicate={dupQ}
            draggable={editable} onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} />
        ))}
        {t.questions.length === 0 && <p className="text-slate-400 text-sm">No questions yet.</p>}
      </div>

      {editable && (
        <>
          <div className="flex gap-2">
            <Button data-testid="add-from-library" variant="outline" onClick={() => setLibOpen(true)} className="font-semibold"><BookOpen size={16} weight="bold" className="mr-1" />Add from Question Library</Button>
          </div>
          <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-4 flex flex-col gap-2">
            <h3 className="font-body font-semibold text-slate-700">Add Question</h3>
            <Input data-testid="q-prompt" placeholder="Question text" value={qForm.prompt} onChange={(e) => setQForm({ ...qForm, prompt: e.target.value })} />
            <div className="flex gap-2 items-center flex-wrap">
              <select data-testid="q-type" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={qForm.answer_type} onChange={(e) => setQForm({ ...qForm, answer_type: e.target.value })}>
                {ANSWER_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              {qForm.answer_type === "number" && (
                <select data-testid="q-unit" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={qForm.unit} onChange={(e) => setQForm({ ...qForm, unit: e.target.value })}>
                  {METER_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              )}
              <select data-testid="q-severity" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={qForm.fail_severity} onChange={(e) => setQForm({ ...qForm, fail_severity: e.target.value })}>
                {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid="q-required" checked={qForm.is_mandatory} onChange={(e) => setQForm({ ...qForm, is_mandatory: e.target.checked })} />Required</label>
              <Button data-testid="add-question" onClick={addQuestion} disabled={!qForm.prompt.trim()} className="bg-blue-600 hover:bg-blue-700 font-semibold ml-auto"><Plus size={16} weight="bold" className="mr-1" />Add</Button>
            </div>
          </div>
        </>
      )}

      <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="assign-panel">
        <h3 className="font-body font-semibold text-slate-700 mb-1">Assign to assets</h3>
        <p className="text-xs text-slate-500 mb-3">Tick one or many. Select a single item for individual assignment, or several for bulk assignment.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-bold uppercase text-slate-500 mb-1">Asset types</div>
            <div className="flex flex-col gap-1 max-h-56 overflow-y-auto border-2 border-slate-200 rounded p-2">
              {assetTypes.map((a) => {
                const already = assignedTypeIds.has(a.id);
                return (
                  <label key={a.id} data-testid={`assign-type-${a.id}`} className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${already ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                    <input type="checkbox" disabled={already} checked={!!selTypes[a.id]} onChange={() => setSelTypes((s) => ({ ...s, [a.id]: !s[a.id] }))} />
                    <span className="text-slate-800">{a.name}</span>{already && <span className="text-[9px] font-bold uppercase text-emerald-600">assigned</span>}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase text-slate-500 mb-1">Specific assets</div>
            <Input data-testid="assign-asset-search" placeholder="Search number, name, reg…" value={assetQ} onChange={(e) => setAssetQ(e.target.value)} className="h-9 mb-1" />
            <div className="flex flex-col gap-1 max-h-48 overflow-y-auto border-2 border-slate-200 rounded p-2">
              {assets.filter((a) => { const s = assetQ.toLowerCase(); return !s || `${a.asset_number} ${a.name} ${a.registration || ""}`.toLowerCase().includes(s); }).map((a) => {
                const already = assignedAssetIds.has(a.id);
                return (
                  <label key={a.id} data-testid={`assign-asset-${a.id}`} className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${already ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                    <input type="checkbox" disabled={already} checked={!!selAssets[a.id]} onChange={() => setSelAssets((s) => ({ ...s, [a.id]: !s[a.id] }))} />
                    <span className="text-slate-800">{a.asset_number} · {a.name}</span>{already && <span className="text-[9px] font-bold uppercase text-emerald-600">assigned</span>}
                  </label>
                );
              })}
              {assets.length === 0 && <p className="text-xs text-slate-400">No assets.</p>}
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 mt-2">
          <input type="checkbox" data-testid="assign-follow-latest" checked={followLatest} onChange={(e) => setFollowLatest(e.target.checked)} />
          Follow latest published version (auto-updates when you publish a new revision).
        </label>
        <Button data-testid="assign-bulk-btn" onClick={bulkAssign} disabled={typeChosen.length + assetChosen.length === 0} className="mt-2 bg-slate-900 hover:bg-slate-800 font-semibold disabled:opacity-50">
          Assign selected ({typeChosen.length + assetChosen.length})
        </Button>
        <div className="flex flex-wrap gap-1 mt-3">{(t.assignments || []).map((a) => (
          <span key={a.id} data-testid={`assignment-${a.id}`} className="text-xs bg-slate-100 rounded px-2 py-0.5 inline-flex items-center gap-1">
            {a.asset_type_id ? (assetTypes.find((x) => x.id === a.asset_type_id)?.name || "type") : a.asset_id ? `#${assets.find((x) => x.id === a.asset_id)?.asset_number || "asset"}` : "model"}
            <span className={`text-[9px] font-bold uppercase px-1 rounded ${a.follow_latest === false ? "bg-slate-300 text-slate-700" : "bg-blue-100 text-blue-700"}`}>{a.follow_latest === false ? "pinned" : "latest"}</span>
            <button data-testid={`unassign-${a.id}`} onClick={() => unassign(a.id)} className="text-slate-400 hover:text-red-600 font-bold">×</button>
          </span>
        ))}</div>
      </div>

      {libOpen && <LibraryDialog assetKind={t.asset_kind} onClose={() => setLibOpen(false)} onAdd={async (ids) => {
        try { await api.post(`/checks/templates/${id}/add-from-library`, { item_ids: ids }); toast.success(`Added ${ids.length} question${ids.length === 1 ? "" : "s"}`); setLibOpen(false); reload(); }
        catch { toast.error("Failed"); }
      }} />}
    </div>
  );
}

function newQ() { return { prompt: "", answer_type: "pass_fail", is_mandatory: true, fail_severity: "", unit: "" }; }

function QuestionRow({ q, index, total, editable, onMove, onSave, onDelete, onDuplicate, draggable, onDragStart, onDragOver, onDrop }) {
  const [f, setF] = useState(q);
  const [adv, setAdv] = useState(false);
  useEffect(() => { setF(q); }, [q]);
  return (
    <div data-testid={`question-${q.id}`} draggable={draggable} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
      className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-col gap-2">
      <div className="flex items-start gap-2">
        {editable && <span className="text-slate-300 pt-2 cursor-grab" title="Drag to reorder"><DotsSixVertical size={18} weight="bold" /></span>}
        <span className="font-mono text-xs text-slate-400 pt-2.5">#{index + 1}</span>
        <Input data-testid={`q-prompt-${q.id}`} value={f.prompt || ""} disabled={!editable} onChange={(e) => setF({ ...f, prompt: e.target.value })} className="flex-1" />
        {editable && (
          <div className="flex flex-col">
            <button data-testid={`q-up-${q.id}`} onClick={() => onMove(index, "up")} disabled={index === 0} className="text-slate-500 disabled:opacity-30"><CaretUp size={16} weight="bold" /></button>
            <button data-testid={`q-down-${q.id}`} onClick={() => onMove(index, "down")} disabled={index === total - 1} className="text-slate-500 disabled:opacity-30"><CaretDown size={16} weight="bold" /></button>
          </div>
        )}
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <select data-testid={`q-type-${q.id}`} value={f.answer_type} disabled={!editable} onChange={(e) => setF({ ...f, answer_type: e.target.value })} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
          {ANSWER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {f.answer_type === "number" && (
          <select data-testid={`q-unit-${q.id}`} value={f.unit || ""} disabled={!editable} onChange={(e) => setF({ ...f, unit: e.target.value })} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
            {METER_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
        )}
        <select data-testid={`q-sev-${q.id}`} value={f.fail_severity || ""} disabled={!editable} onChange={(e) => setF({ ...f, fail_severity: e.target.value })} className="h-9 border-2 border-slate-300 rounded px-2 text-sm">
          {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {f.fail_severity && <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${sevBadgeCls(f.fail_severity)}`}>{f.fail_severity}</span>}
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.is_mandatory} disabled={!editable} onChange={(e) => setF({ ...f, is_mandatory: e.target.checked })} />Required</label>
        <Input placeholder="Section" data-testid={`q-section-${q.id}`} value={f.section || ""} disabled={!editable} onChange={(e) => setF({ ...f, section: e.target.value })} className="w-32 h-9" />
        {editable && <button data-testid={`q-advanced-${q.id}`} onClick={() => setAdv((v) => !v)} className="text-xs font-semibold text-blue-600 ml-auto">{adv ? "Hide" : "Advanced"} Options</button>}
      </div>
      {adv && editable && (
        <div className="flex flex-col gap-2 bg-slate-50 rounded p-2">
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid={`q-createsfault-${q.id}`} checked={!!f.creates_fault} onChange={(e) => setF({ ...f, creates_fault: e.target.checked })} />Failure creates a fault</label>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.requires_photo} onChange={(e) => setF({ ...f, requires_photo: e.target.checked })} />Require a photo</label>
          <Textarea placeholder="Guidance for the operative (optional)" value={f.guidance || ""} onChange={(e) => setF({ ...f, guidance: e.target.value })} className="border-2 text-sm" />
        </div>
      )}
      {editable && (
        <div className="flex gap-2">
          <Button data-testid={`q-save-${q.id}`} onClick={() => onSave(q.id, { prompt: f.prompt, answer_type: f.answer_type, section: f.section, unit: f.unit, fail_severity: f.fail_severity || null, is_mandatory: f.is_mandatory, creates_fault: f.creates_fault, requires_photo: f.requires_photo, guidance: f.guidance })} className="h-8 bg-blue-600 hover:bg-blue-700 font-semibold text-xs">Save</Button>
          <Button data-testid={`q-dup-${q.id}`} variant="outline" onClick={() => onDuplicate(q.id)} className="h-8 font-semibold text-xs"><Copy size={13} weight="bold" className="mr-1" />Duplicate</Button>
          <Button data-testid={`q-del-${q.id}`} variant="outline" onClick={() => onDelete(q.id)} className="h-8 font-semibold text-xs text-red-600"><Trash size={13} weight="bold" className="mr-1" />Delete</Button>
        </div>
      )}
    </div>
  );
}

function LibraryDialog({ assetKind, onClose, onAdd }) {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState(assetKind || "");
  const [sel, setSel] = useState({});
  useEffect(() => {
    const p = kind ? `?asset_kind=${kind}` : "";
    api.get(`/checks/library${p}`).then((r) => setItems(r.data)).catch(() => {});
  }, [kind]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="library-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">Question Library</DialogTitle></DialogHeader>
        <select data-testid="library-kind" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All types</option>
          {["vehicle", "trailer", "plant", "tool", "chainsaw", "climbing", "other"].map((k) => <option key={k} value={k} className="capitalize">{k}</option>)}
        </select>
        <div className="flex flex-col gap-1 mt-2">
          {items.map((q) => (
            <label key={q.id} data-testid={`lib-item-${q.id}`} className={`flex items-start gap-2 rounded border-2 p-2 cursor-pointer ${sel[q.id] ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`}>
              <input type="checkbox" checked={!!sel[q.id]} onChange={() => toggle(q.id)} className="mt-1" />
              <span className="flex-1 font-body text-sm text-slate-800">{q.prompt}
                <span className="ml-2 text-[10px] font-mono text-slate-400">{typeLabel(q.answer_type)}</span>
                {q.fail_severity && <span className={`ml-1 text-[9px] font-bold uppercase px-1 rounded ${sevBadgeCls(q.fail_severity)}`}>{q.fail_severity}</span>}</span>
            </label>
          ))}
          {items.length === 0 && <p className="text-sm text-slate-400 py-2">No library questions.</p>}
        </div>
        <Button data-testid="library-add" disabled={chosen.length === 0} onClick={() => onAdd(chosen)} className="bg-blue-600 hover:bg-blue-700 font-semibold mt-2">Add {chosen.length || ""} to checklist</Button>
      </DialogContent>
    </Dialog>
  );
}
